import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from "react";
import { LocalStorage } from "src/services/LocalStorage";

type DebugStorageKey = "debugProfilingEnabled";

type DebugContextType = {
    isProfilingEnabled: boolean;
    toggleProfiling: () => void;
};

const DebugContext = createContext<DebugContextType | undefined>(undefined);

type DebugProviderProps = {
    children: ReactNode;
};

export const DebugProvider = ({ children }: DebugProviderProps) => {
    const [isProfilingEnabled, setIsProfilingEnabled] = useState<boolean>(() => {
        const stored = LocalStorage.getItem("debugProfilingEnabled" as DebugStorageKey);
        return stored === "true" ? true : false;
    });

    const toggleProfiling = useCallback(() => {
        setIsProfilingEnabled((prev) => {
            const newValue = !prev;
            LocalStorage.setItem("debugProfilingEnabled" as DebugStorageKey, String(newValue));
            return newValue;
        });
    }, []);

    const value = useMemo(
        () => ({
            isProfilingEnabled,
            toggleProfiling,
        }),
        [isProfilingEnabled, toggleProfiling],
    );

    return <DebugContext.Provider value={value}>{children}</DebugContext.Provider>;
};

export const useDebugContext = (): DebugContextType => {
    const context = useContext(DebugContext);
    if (context === undefined) {
        throw new Error("useDebugContext must be used within a DebugProvider");
    }
    return context;
};
